import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, InputPassword, SparkIcon } from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import '../settings.css';

// Max one target per alias — alias is locked, not user-selectable.

// Mirrors deployment_config_builder._DOCKERHUB_REPOSITORY_RE
const DOCKERHUB_REPOSITORY_RE =
    /^[a-z0-9][a-z0-9._-]{0,29}\/[a-z0-9][a-z0-9._-]{0,127}$/;

function validateDockerHubRepository(repository) {
    const trimmed = (repository || '').trim().toLowerCase();
    if (!trimmed) return 'Repository is required';
    if (!DOCKERHUB_REPOSITORY_RE.test(trimmed)) {
        return 'Use <namespace>/<repository> (lowercase)';
    }
    return null;
}

function normalizeTargets(targets) {
    return (targets || []).map((t) => ({
        repository: t.repository || '',
        alias: t.alias || 'dev',
    }));
}

function targetsEqual(a, b) {
    if (a.length !== b.length) return false;
    return a.every((t, i) => {
        const o = b[i];
        return (
            (t.repository || '') === (o.repository || '') &&
            (t.alias || '') === (o.alias || '')
        );
    });
}

/**
 * Docker Hub push targets (deployment) settings, Spark rebuild of the
 * Figma "External Deploy - Targets" panel (frame 6026-722): Account
 * credentials (username + PAT), Targets rows (input + alias + trash),
 * "+ Add Dev/Prod Target" and a "Save Changes" footer.
 */
const DockerHubCredentialsForm = ({ settings, onDockerHubSettingsUpdate }) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';

    const [username, setUsername] = useState(settings?.dockerhub_username || '');
    const [patDraft, setPatDraft] = useState('');
    const [patTouched, setPatTouched] = useState(false);
    const [targets, setTargets] = useState(normalizeTargets(settings?.dockerhub_targets));
    const [isSaving, setIsSaving] = useState(false);

    const patConfigured = Boolean(settings?.dockerhub_pat_configured);

    useEffect(() => {
        setUsername(settings?.dockerhub_username || '');
    }, [settings?.dockerhub_username]);

    useEffect(() => {
        setTargets(normalizeTargets(settings?.dockerhub_targets));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(settings?.dockerhub_targets || [])]);

    const savedTargets = useMemo(
        () => normalizeTargets(settings?.dockerhub_targets),
        [settings?.dockerhub_targets]
    );

    const usernameDirty = (username || '') !== (settings?.dockerhub_username || '');
    const patDirty = patTouched && Boolean((patDraft || '').trim());
    const targetsDirty = !targetsEqual(targets, savedTargets);
    const isDirty = usernameDirty || patDirty || targetsDirty;

    const targetErrors = targets.map((t) => validateDockerHubRepository(t.repository));
    const hasTargetErrors = targetErrors.some(Boolean);
    const missingCredentials =
        !(username || '').trim() || (!patConfigured && !(patDraft || '').trim());

    const handleTargetChange = (index, key, value) => {
        setTargets((prev) =>
            prev.map((t, i) => (i === index ? { ...t, [key]: value } : t))
        );
    };

    const hasDevTarget = targets.some((t) => t.alias === 'dev');
    const hasProdTarget = targets.some((t) => t.alias === 'prod');

    const handleAddTarget = (alias) => {
        setTargets((prev) => [...prev, { repository: '', alias }]);
    };

    const handleRemoveTarget = (index) => {
        setTargets((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        if (hasTargetErrors || missingCredentials) return;
        const fields = {
            dockerhub_username: (username || '').trim(),
            dockerhub_targets: targets.map((t) => ({
                repository: (t.repository || '').trim().toLowerCase(),
                alias: t.alias || 'dev',
            })),
            dockerhub_pat_update: patDirty,
            dockerhub_pat: patDirty ? (patDraft || '').trim() : null,
        };
        setIsSaving(true);
        onDockerHubSettingsUpdate(fields, 'saved');
        setPatDraft('');
        setPatTouched(false);
        setTimeout(() => setIsSaving(false), 500);
    };

    return (
        <div className="settings-providers__panel">
            <p className="settings-text settings-providers__description fs-unmask">
                The username and PAT below apply to all targets. Use a Docker
                Hub personal access token (PAT) with push permission — not your
                account password.
            </p>

            <h3 className="settings-subheading fs-unmask">Account credentials</h3>

            <div className="settings-form">
                <Input
                    size="large"
                    label="Docker Hub username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="my-org-or-user"
                    disabled={!isAdmin}
                    className="settings-secret__input"
                />
                <InputPassword
                    size="large"
                    label="Personal access token (PAT)"
                    showPrefixIcon
                    value={patDraft}
                    onChange={(e) => {
                        setPatDraft(e.target.value);
                        setPatTouched(true);
                    }}
                    placeholder={patConfigured ? '••••••••••••••••' : 'dckr_pat_…'}
                    disabled={!isAdmin}
                    showHelperText
                    helperText={
                        patConfigured && !patDirty
                            ? 'A token is saved. Enter a new value only to rotate it.'
                            : 'Create a PAT under Docker Hub → Account Settings → Security.'
                    }
                    className="settings-secret__input"
                />
            </div>

            <h3 className="settings-subheading fs-unmask">Targets</h3>

            {targets.length === 0 ? (
                <p className="settings-text fs-unmask">
                    No Docker Hub targets configured yet. Add one to enable
                    &quot;Push to Docker Hub&quot;.
                </p>
            ) : null}

            {targets.map((target, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="settings-target-row">
                    <div className="settings-target-row__field">
                        <Input
                            size="large"
                            showLabel={false}
                            value={target.repository}
                            onChange={(e) =>
                                handleTargetChange(index, 'repository', e.target.value)
                            }
                            placeholder="myorg/specificai-inference"
                            disabled={!isAdmin}
                            className="settings-secret__input"
                        />
                        {targetErrors[index] ? (
                            <p className="settings-field-error">{targetErrors[index]}</p>
                        ) : null}
                    </div>
                    <span className="settings-target-row__alias">{target.alias}</span>
                    <Button
                        size="tiny"
                        variant="ghost"
                        iconSlug="trash"
                        aria-label="Remove target"
                        startIcon={<SparkIcon slug="trash" size="small" />}
                        onClick={() => handleRemoveTarget(index)}
                        disabled={!isAdmin}
                    />
                </div>
            ))}

            <div className="settings-form__row">
                {!hasDevTarget ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('dev')}
                        disabled={!isAdmin}
                    >
                        Add Dev Target
                    </Button>
                ) : null}
                {!hasProdTarget ? (
                    <Button
                        size="tiny"
                        variant="ghost"
                        showStartIcon
                        onClick={() => handleAddTarget('prod')}
                        disabled={!isAdmin}
                    >
                        Add Prod Target
                    </Button>
                ) : null}
            </div>

            <div className="settings-form__footer">
                <Button
                    size="tiny"
                    variant="primary"
                    onClick={handleSave}
                    disabled={!isAdmin || !isDirty || hasTargetErrors || missingCredentials || isSaving}
                    disableWhenReadOnly
                >
                    Save Changes
                </Button>
            </div>
        </div>
    );
};

export default DockerHubCredentialsForm;
